import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaClock, FaCheckCircle, FaTimes, FaEye } from 'react-icons/fa';

export default function MyListings() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    category_id: '',
    listing_type: 'product',
    price: '',
    location: '',
    images: []
  });

  useEffect(() => {
    if (user) {
      fetchListings();
      fetchCategories();
    }
  }, [user]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/user-listings/my-listings?user_id=${user.id}`);
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null
      };

      if (editListing) {
        await axios.put(
          `${API}/user-listings/update/${editListing.id}?user_id=${user.id}`,
          data
        );
        toast.success(i18n.language === 'fr' ? 'Annonce mise à jour' : 'Listing updated');
      } else {
        await axios.post(
          `${API}/user-listings/create?user_id=${user.id}`,
          data
        );
        toast.success(i18n.language === 'fr' ? 'Annonce créée' : 'Listing created');
      }

      setDialogOpen(false);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error('Error saving listing:', error);
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' 
        ? 'Erreur lors de l\'enregistrement' 
        : 'Error saving listing'));
    }
  };

  const handleEdit = (listing) => {
    setEditListing(listing);
    setFormData({
      title_fr: listing.title_fr,
      title_en: listing.title_en,
      description_fr: listing.description_fr,
      description_en: listing.description_en,
      category_id: listing.category_id,
      listing_type: listing.listing_type,
      price: listing.price || '',
      location: listing.location || '',
      images: listing.images || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(i18n.language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette annonce ?' 
      : 'Are you sure you want to delete this listing?')) {
      try {
        await axios.delete(`${API}/user-listings/delete/${id}?user_id=${user.id}`);
        toast.success(i18n.language === 'fr' ? 'Annonce supprimée' : 'Listing deleted');
        fetchListings();
      } catch (error) {
        console.error('Error deleting listing:', error);
        toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting listing');
      }
    }
  };

  const resetForm = () => {
    setEditListing(null);
    setFormData({
      title_fr: '',
      title_en: '',
      description_fr: '',
      description_en: '',
      category_id: '',
      listing_type: 'product',
      price: '',
      location: '',
      images: []
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-sm flex items-center gap-1">
            <FaCheckCircle /> {i18n.language === 'fr' ? 'Approuvé' : 'Approved'}
          </span>
        );
      case 'pending':
        return (
          <span className="px-3 py-1 bg-[#00D4FF]/10 text-orange-700 rounded-full text-sm flex items-center gap-1">
            <FaClock /> {i18n.language === 'fr' ? 'En attente' : 'Pending'}
          </span>
        );
      case 'rejected':
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm flex items-center gap-1">
            <FaTimes /> {i18n.language === 'fr' ? 'Rejeté' : 'Rejected'}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540]">
            {i18n.language === 'fr' ? 'Mes Annonces' : 'My Listings'}
          </h1>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#635BFF]">
                <FaPlus className="mr-2" />
                {i18n.language === 'fr' ? 'Nouvelle Annonce' : 'New Listing'}
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editListing 
                    ? (i18n.language === 'fr' ? 'Modifier l\'annonce' : 'Edit Listing')
                    : (i18n.language === 'fr' ? 'Nouvelle annonce' : 'New Listing')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Titre (FR)</Label>
                    <Input 
                      required 
                      value={formData.title_fr} 
                      onChange={(e) => setFormData({...formData, title_fr: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Title (EN)</Label>
                    <Input 
                      required 
                      value={formData.title_en} 
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Description (FR)</Label>
                    <textarea
                      required
                      value={formData.description_fr}
                      onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                      className="w-full min-h-[80px] p-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <Label>Description (EN)</Label>
                    <textarea
                      required
                      value={formData.description_en}
                      onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                      className="w-full min-h-[80px] p-2 border rounded-md"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Catégorie</Label>
                    <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Type</Label>
                    <Select value={formData.listing_type} onValueChange={(value) => setFormData({...formData, listing_type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Produit</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="rental">Location</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Prix (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={formData.price} 
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Localisation</Label>
                  <Input 
                    value={formData.location} 
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="ex: Paris, France"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button type="submit" className="bg-[#0A2540]">
                    {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Listings Grid */}
        {listings.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">
              {i18n.language === 'fr' 
                ? 'Vous n\'avez pas encore d\'annonces' 
                : 'You don\'t have any listings yet'}
            </p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#635BFF]">
              <FaPlus className="mr-2" />
              {i18n.language === 'fr' ? 'Créer ma première annonce' : 'Create my first listing'}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => (
              <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg">
                      {i18n.language === 'fr' ? listing.title_fr : listing.title_en}
                    </h3>
                    {getStatusBadge(listing.status)}
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {i18n.language === 'fr' ? listing.description_fr : listing.description_en}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>{listing.listing_type}</span>
                    {listing.price && <span className="font-bold text-[#0A2540]">{listing.price} €</span>}
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <FaEye /> {listing.views} {i18n.language === 'fr' ? 'vues' : 'views'}
                    </span>
                    <span>{new Date(listing.created_at).toLocaleDateString()}</span>
                  </div>

                  {listing.admin_notes && listing.status === 'rejected' && (
                    <div className="mb-4 p-3 bg-red-50 rounded text-sm text-red-700">
                      <strong>{i18n.language === 'fr' ? 'Raison :' : 'Reason:'}</strong> {listing.admin_notes}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleEdit(listing)} 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                    >
                      <FaEdit className="mr-1" /> {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                    </Button>
                    <Button 
                      onClick={() => handleDelete(listing.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
